import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { renderMarkdown } from 'monaco-editor/esm/vs/base/browser/markdownRenderer';
import { RichTextType, RichTextValue as RichTextValueDto } from '../services/api.services';
import { escapeHtml } from '../shared/utils';

const renderedHtmlCache = new Map<string, SafeHtml>();
const renderedMarkdownCache = new Map<string, SafeHtml>();

export class RichTextValue {
  public static readonly empty = new RichTextValue({
    type: RichTextType.String,
    content: ''
  });


  constructor(public readonly dto: RichTextValueDto) { }

  get type() { return this.dto.type; }
  get content() { return this.dto.content; }

  private _html: SafeHtml;

  get html(): SafeHtml {
    if (!this.content?.length)
      return '';

    this._html ??= this.render();
    return this._html;
  }

  private render() {
    let html;

    if (this.type == RichTextType.String) {
      html = `<span>${escapeHtml(this.content)}</span>`;
      html = RichTextValue.domSanitizer.bypassSecurityTrustHtml(html);
      renderedHtmlCache.set(this.content, html);
      return html;
    }

    if (this.type == RichTextType.Markdown) {
      let cachedValue = renderedMarkdownCache.get(this.content);
      if (cachedValue)
        return cachedValue;
      let renderer = renderMarkdown({ value: this.content });
      try {
        html = RichTextValue.domSanitizer.bypassSecurityTrustHtml(renderer.element.outerHTML);
        renderedMarkdownCache.set(this.content, html);
        return html;
      }
      finally {
        renderer.dispose();
      }
    }
  }
}

export namespace RichTextValue {
  export let domSanitizer: DomSanitizer;

  class Cache {
    readonly entries = new Map<string, RichTextValue>();

    constructor(readonly type: RichTextType) { }

    getOrCreate(content: string) {
      if (content == '')
        return RichTextValue.empty;

      let result = this.entries.get(content);
      if (!result) {
        result = new RichTextValue({
          type: this.type,
          content
        });

        this.entries.set(content, result);
      }

      return result;
    }
  }

  const textCache = new Cache(RichTextType.String);
  const htmlCache = new Cache(RichTextType.HTML);
  const markdownCache = new Cache(RichTextType.Markdown);

  export function text(content: string): RichTextValue {
    return textCache.getOrCreate(content);
  }

  export function html(content: string): RichTextValue {
    return htmlCache.getOrCreate(content);
  }

  export function markdown(content: string): RichTextValue {
    return markdownCache.getOrCreate(content);
  }

  export function from(dto: RichTextValueDto | null): RichTextValue | null {
    return dto?.type == RichTextType.String   ? text(dto.content)     :
           dto?.type == RichTextType.HTML     ? html(dto.content)     :
           dto?.type == RichTextType.Markdown ? markdown(dto.content) : null;
  }
}
